import { FilterOutlined } from "@ant-design/icons";
import { App, Badge, Button, Flex, Modal } from "antd";
import Search from "antd/es/input/Search";
import { FC, useEffect, useState } from "react";
import { getApiError, getStorageInventoryGet } from "tm-shared/api";
import {
    ContractProduct,
    ContractType,
    DiscountProduct,
    Point,
} from "tm-shared/types";
import { isPointDistribution } from "tm-shared/utils";

import { FilterDrawerUI, useCatalog, useFilters } from "@/domains/product";
import { useDebounce, useInfiniteScroll, useQueryFilter } from "@/hooks";
import { PrimaryText } from "@/shared/ui";

import { CheckboxProductRow } from "./CheckboxProductRow";
import {
    StyledContentBody,
    StyledContentList,
    StyledContentListContainer,
} from "./Styled";

interface AddProductsModalProps {
    contractType: ContractType.PaidProductInput;
    open: boolean;
    onClose: () => void;
    selectedProducts: ContractProduct[];
    onAdd: (product: ContractProduct) => void;
    onRemove: (barcode: string) => void;
    point: Point | null;
    pointLoading: boolean;
}

interface AddBarcodesModalProps {
    contractType: ContractType.AssortmentMatrix;
    open: boolean;
    onClose: () => void;
    selectedProducts: string[];
    onAdd: (barcode: string) => void;
    onRemove: (barcode: string) => void;
    point: Point | null;
    pointLoading: boolean;
}

interface AddDiscountsModalProps {
    contractType: ContractType.Discount;
    open: boolean;
    onClose: () => void;
    selectedProducts: DiscountProduct[];
    onAdd: (product: DiscountProduct) => void;
    onRemove: (barcode: string) => void;
    point: Point | null;
    pointLoading: boolean;
}

export const AddProductsModal: FC<
    AddProductsModalProps | AddBarcodesModalProps | AddDiscountsModalProps
> = ({
    contractType,
    onClose,
    open,
    selectedProducts,
    onAdd,
    onRemove,
    point,
    pointLoading,
}) => {
    const { notification } = App.useApp();

    const storageId =
        point && isPointDistribution(point)
            ? point?.typeContext?.storage?.id
            : null;

    const [inventoryList, setInventoryList] = useState<Record<string, number>>(
        {},
    );
    const [inventoryLoading, setInventoryLoading] = useState(false);

    useEffect(() => {
        if (!storageId || !open) return;

        const fetchInventory = async () => {
            try {
                setInventoryLoading(true);
                const { result } = await getStorageInventoryGet({ storageId });
                setInventoryList(result);
            } catch (error) {
                console.error("Failed to load storage inventory:", error);
                const { displayMessage } = getApiError(error);
                notification.error({
                    title: "Не вдалося завантажити залишки",
                    description: displayMessage,
                });
            } finally {
                setInventoryLoading(false);
            }
        };

        fetchInventory();
    }, [notification, storageId, open]);

    const isInventoryLoading = open && (pointLoading || inventoryLoading);

    const { products } = useCatalog();

    const [query, setQuery] = useState("");
    const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
    const [hideZeroProducts, setHideZeroProducts] = useState(false);

    const [filters, setFilters] = useState<{
        [key: string]: (string | number | boolean)[];
    }>({});

    const isProductSelected = (barcode: string) => {
        switch (contractType) {
            case ContractType.PaidProductInput:
            case ContractType.Discount:
                return selectedProducts.some((p) => p.barcode === barcode);
            case ContractType.AssortmentMatrix:
                return selectedProducts.some((p) => p === barcode);
        }
    };

    const handleCheckboxChange = (
        product: ContractProduct,
        checked: boolean,
    ) => {
        if (checked) {
            switch (contractType) {
                case ContractType.PaidProductInput:
                    onAdd(product);
                    break;
                case ContractType.AssortmentMatrix:
                    onAdd(product.barcode);
                    break;
                case ContractType.Discount:
                    onAdd({
                        barcode: product.barcode,
                        price: 0,
                    });
            }
        } else {
            onRemove(product.barcode);
        }
    };

    const { filteredProducts, availableFilters } = useFilters({
        products,
        filters: filters,
        inventoryList,
        hideZeroProducts,
    });

    const debouncedQuery = useDebounce(query);

    const searchFilteredProducts = useQueryFilter(
        filteredProducts,
        debouncedQuery,
        [(p) => p.name, (p) => p.barcode],
    );
    const { visibleItems, loadMore } = useInfiniteScroll(
        searchFilteredProducts,
    );

    const onCloseFilterDrawer = () => setOpenFilterDrawer(false);

    const activeFiltersCount = Object.values(filters).filter(
        (values) => values.length > 0,
    ).length;

    return (
        <>
            <Modal
                title={"Додати товар"}
                open={open}
                onCancel={onClose}
                centered={true}
                footer={[]}
                mask={{ closable: false }}
                styles={{ body: { height: "90vh" }, footer: { margin: 0 } }}
            >
                <StyledContentBody>
                    <Flex gap={8} align="center">
                        <Search
                            placeholder="Пошук"
                            allowClear
                            size="large"
                            onChange={(evt) => setQuery(evt.target.value)}
                        />

                        <Badge count={activeFiltersCount}>
                            <Button
                                onClick={() => setOpenFilterDrawer(true)}
                                type="text"
                                icon={
                                    <FilterOutlined style={{ fontSize: 24 }} />
                                }
                            />
                        </Badge>
                    </Flex>

                    <StyledContentListContainer>
                        {visibleItems.length > 0 ? (
                            <StyledContentList vertical gap={16}>
                                {visibleItems.map((product, index) => {
                                    const isChecked = isProductSelected(
                                        product.barcode,
                                    );
                                    const isLast =
                                        index === visibleItems.length - 1;

                                    return (
                                        <CheckboxProductRow
                                            key={product.barcode}
                                            checked={isChecked}
                                            product={product}
                                            itemsLeft={
                                                inventoryList[product.barcode]
                                            }
                                            itemsLeftLoading={
                                                isInventoryLoading
                                            }
                                            onChange={handleCheckboxChange}
                                            isLast={isLast}
                                            onInView={() => loadMore()}
                                        />
                                    );
                                })}
                            </StyledContentList>
                        ) : (
                            <PrimaryText>Товарів не знайдено</PrimaryText>
                        )}
                    </StyledContentListContainer>
                </StyledContentBody>
            </Modal>

            <FilterDrawerUI
                isOpen={openFilterDrawer}
                onClose={onCloseFilterDrawer}
                onApply={onCloseFilterDrawer}
                onReset={() => {
                    setFilters({});
                    setHideZeroProducts(false);
                    onCloseFilterDrawer();
                }}
                availableFilters={availableFilters}
                selectedFilters={filters}
                products={products}
                availableProductsLength={filteredProducts.length}
                onFilterChange={(
                    tag: string,
                    values: (string | number | boolean)[],
                ) => {
                    setFilters((prev) => ({
                        ...prev,
                        [tag]: values,
                    }));
                }}
                hideZeroProducts={hideZeroProducts}
                onToggleHideZeroProducts={() =>
                    setHideZeroProducts((prev) => !prev)
                }
            />
        </>
    );
};
